import { RateLandedCost } from "@redotech/redo-fulfillment-customs-common/rate-landed-cost";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { getLcgInfo, landedCostMajorUnits } from "../common/lcg-info";

export interface InternationalFees {
  dutiesAndFees: number;
  taxes: number;
  total: number;
}

export interface MerchantInternationalFees extends InternationalFees {
  currency: string;
}

export function getMarginBreakdown(
  fulfillmentGroup: FulfillmentGroupData,
  selectedRate: number,
  rateLandedCost?: RateLandedCost | null,
): {
  fulfillmentOrdersBreakdown: {
    labelQuotedCost: number;
    shippingPaid: number;
    percentageMarkup: string;
    percentageMarkupCost: number;
    fixedMarkup: number;
  }[];
  customerInternationalFees: InternationalFees | null;
  merchantInternationalFees: MerchantInternationalFees | null;
  totalMargin: number;
} {
  const fulfillmentOrdersBreakdown: {
    labelQuotedCost: number;
    shippingPaid: number;
    percentageMarkup: string;
    percentageMarkupCost: number;
    fixedMarkup: number;
  }[] =
    fulfillmentGroup.totalShippingCost.fulfillmentOrdersBreakdown?.map(
      (order) => {
        const labelQuotedCost = Number(order.labelCost);
        const percentageMarkup = order.markup?.percentage || "0";
        const percentageMarkupCost =
          (Number(percentageMarkup) / 100) * labelQuotedCost;
        const fixedMarkup = Number(order.markup?.amount || "0");

        const shippingPaid =
          labelQuotedCost + percentageMarkupCost + fixedMarkup;

        return {
          shippingPaid,
          percentageMarkup,
          fixedMarkup,
          labelQuotedCost,
          percentageMarkupCost,
        };
      },
    ) || [];

  const groupLandedCost = fulfillmentGroup.landedCost;
  const customerInternationalFees: InternationalFees | null = groupLandedCost
    ? {
        dutiesAndFees: groupLandedCost.duties + groupLandedCost.fees,
        taxes: groupLandedCost.taxes,
        total:
          groupLandedCost.duties + groupLandedCost.taxes + groupLandedCost.fees,
      }
    : null;

  const lcgInfo = getLcgInfo({ landedCost: rateLandedCost });
  const merchantLandedCost = landedCostMajorUnits(lcgInfo);
  const merchantInternationalFees: MerchantInternationalFees | null =
    lcgInfo.showLandedCost
      ? {
          dutiesAndFees: lcgInfo.duties + lcgInfo.fees,
          taxes: lcgInfo.taxes,
          total: merchantLandedCost,
          currency: lcgInfo.currency,
        }
      : null;

  /**
   * Shipping revenue has the customer's landed cost baked in, so the merchant's
   * quoted landed cost is subtracted too. Mirrors the rate dropdown.
   */
  const totalMargin =
    Number(fulfillmentGroup.totalShippingCost.amount) -
    selectedRate -
    merchantLandedCost;

  return {
    fulfillmentOrdersBreakdown,
    customerInternationalFees,
    merchantInternationalFees,
    totalMargin,
  };
}
